import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import {ModalType, TeamRegion} from "data/enums";
import {
	chain,
	chunk,
	compact,
	concat,
	eq,
	fill,
	includes,
	isEmpty,
	noop,
	size,
	take,
	lt,
	uniq,
} from "lodash";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {ICreateTeamPayload, ITeamApiProvider} from "data/providers/api/team.api.provider";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";

export interface IPlayerTooltip {
	isOpen: boolean;
	message: string;
}

const DEFAULT_LINEUP = fill(Array(12), 0);

export interface ITeamStore {
	get i18n(): ILocalizationStore;

	get selectedRegion(): TeamRegion;

	get firstSelectedRegion(): TeamRegion;

	get isSelectedStart(): boolean;

	get onInitTopRankPlayers(): number[];

	get removedTopRankPlayers(): number[];

	get topRankPlayers(): number[];

	get currentLineup(): number[];

	get allLineup(): number[];

	get currentStep(): number;

	get stepSize(): number;

	get isCurrentLineupFull(): boolean;

	get isCurrentLineupEmpty(): boolean;

	get euLineup(): number[];

	get usLineup(): number[];

	get isSelectTeamCompleted(): boolean;

	get isPromptActive(): boolean;

	get isSkipActive(): boolean;

	get isFinalSaved(): boolean;

	get playerTooltipData(): IPlayerTooltip;

	get isEuTeamSaved(): boolean;

	get isUsTeamSaved(): boolean;

	get isTeamSavedAfterLogin(): boolean;

	setSelectedRegion(value: TeamRegion): void;

	setFirstSelectedRegion(value: TeamRegion): void;

	setPrevStep(): void;
	setNextStep(): void;

	autoFillLineup(region: TeamRegion): void;

	clearLineup(): void;

	removePlayer(playerId: number): void;

	addPlayer(playerId: number): void;

	setLineup(region: TeamRegion, arr: number[]): void;

	setIsSelectedStart(): void;

	clearIsSelectedStart(): void;

	createTeam(payload: ICreateTeamPayload): Promise<void>;

	onCreateTeam(region: TeamRegion): Promise<void>;

	setEuTeamSaved(): void;

	setUsTeamSaved(): void;

	setTopRankPlayers(): void;

	setIsTeamSkipped(): void;

	clearIsTeamSkipped(): void;

	setIsPromptActive(): void;

	clearIsPromptActive(): void;

	setIsSkipActive(): void;

	clearIsSkipActive(): void;

	setIsFinalSaved(): void;

	clearIsFinalSaved(): void;

	toFirstStep(): void;

	clearIsTeamsSaved(): void;

	setRegionTeamSaved(): void;

	setPlayerTooltipData(isOpen: boolean, message?: string): void;

	clearPartLineupByStep(): void;

	setIsTeamSavedAfterLogin(): void;

	clearIsTeamSavedAfterLogin(): void;
}

@injectable()
export class TeamStore implements ITeamStore {
	@observable private _selectedRegion: TeamRegion = TeamRegion.Usa;
	@observable private _firstSelectedRegion: TeamRegion = TeamRegion.Usa;
	@observable private _isSelectedStart: boolean = false;
	@observable private _sizeStep: number = 4;
	@observable private _currentStep: number = 0;
	@observable private _euLineup: number[] = DEFAULT_LINEUP;
	@observable private _usLineup: number[] = DEFAULT_LINEUP;
	@observable private _onInitTopRankPlayers: number[] = [];
	@observable private _topRankPlayers: number[] = [];
	@observable private _removedTopRankPlayers: number[] = [];
	@observable private _isEuTeamSaved: boolean = false;
	@observable private _isUsTeamSaved: boolean = false;
	@observable private _isTeamSkipped: boolean = false;
	@observable private _isPromptActive: boolean = false;
	@observable private _isSkipActive: boolean = false;
	@observable private _isFinalSaved: boolean = false;
	@observable private _isTeamSavedAfterLogin: boolean = false;
	@observable private _playerTooltipData: IPlayerTooltip = {
		isOpen: false,
		message: "",
	};

	get i18n() {
		return this._i18nStore;
	}

	get selectedRegion() {
		return this._selectedRegion;
	}

	get firstSelectedRegion() {
		return this._firstSelectedRegion;
	}

	get isSelectedStart() {
		return this._isSelectedStart;
	}

	get topRankPlayers() {
		return this._topRankPlayers;
	}

	get onInitTopRankPlayers() {
		return this._onInitTopRankPlayers;
	}

	get removedTopRankPlayers() {
		return this._removedTopRankPlayers;
	}

	get euLineup() {
		return this._euLineup;
	}

	get usLineup() {
		return this._usLineup;
	}

	get currentLineup() {
		const fullLineup = eq(this._firstSelectedRegion, TeamRegion.Europe)
			? concat(this._euLineup, this._usLineup)
			: concat(this._usLineup, this._euLineup);

		const chunkedLineup = chunk(fullLineup, 6);

		return chunkedLineup[this._currentStep];
	}

	get isCurrentLineupFull() {
		return size(compact(this.currentLineup)) === 6;
	}

	get isCurrentLineupEmpty() {
		return size(compact(this.currentLineup)) === 0;
	}

	get allLineup() {
		return eq(this.selectedRegion, TeamRegion.Europe)
			? concat(this._euLineup, this._usLineup)
			: concat(this._usLineup, this._euLineup);
	}

	get currentStep() {
		return this._currentStep;
	}

	get stepSize() {
		return this._sizeStep;
	}

	get isSelectTeamCompleted() {
		if (this._isFinalSaved) {
			return true;
		}
		if (this._isTeamSkipped) {
			return [this._isEuTeamSaved, this._isUsTeamSaved].some(Boolean);
		}
		return [this._isEuTeamSaved, this._isUsTeamSaved].every(Boolean);
	}

	get isPromptActive() {
		return this._isPromptActive;
	}

	get isSkipActive() {
		return this._isSkipActive;
	}

	get isFinalSaved() {
		return this._isFinalSaved;
	}

	get playerTooltipData() {
		return this._playerTooltipData;
	}

	get isEuTeamSaved() {
		return this._isEuTeamSaved;
	}

	get isUsTeamSaved() {
		return this._isUsTeamSaved;
	}

	get isTeamSavedAfterLogin() {
		return this._isTeamSavedAfterLogin;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.TeamApiProvider) private _teamApi: ITeamApiProvider,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	@action setEuTeamSaved() {
		this._isEuTeamSaved = true;
	}

	@action setUsTeamSaved() {
		this._isUsTeamSaved = true;
	}

	@action setIsSelectedStart() {
		this._isSelectedStart = true;
	}

	@action clearIsSelectedStart() {
		this._isSelectedStart = false;
	}

	@action setIsPromptActive() {
		this._isPromptActive = true;
	}

	@action clearIsPromptActive() {
		this._isPromptActive = false;
	}

	@action setLineup(region: TeamRegion, arr: number[]) {
		const isEurope = eq(region, TeamRegion.Europe);

		if (isEurope) {
			this._euLineup = arr;
		} else {
			this._usLineup = arr;
		}
	}

	setLocalEuLineup = () => {
		if (this._euLineup.length) {
			localStorage.setItem("euLineup", JSON.stringify(this._euLineup));
			this._isEuTeamSaved = true;
		}
	};

	setLocalUsLineup = () => {
		if (this._usLineup.length) {
			localStorage.setItem("usLineup", JSON.stringify(this._usLineup));
			this._isUsTeamSaved = true;
		}
	};

	onSaveTeam = () => {
		const isFullEuLineup = size(compact(this._euLineup)) === 12;
		const isFullUsLineup = size(compact(this._usLineup)) === 12;
		this.clearIsPromptActive();
		this.setIsFinalSaved();

		if (this._userStore.isAuthorized) {
			if (isFullEuLineup) {
				this._isEuTeamSaved = true;
			}
			if (isFullUsLineup) {
				this._isUsTeamSaved = true;
			}
			// void this.onCreateTeam();
			// this.setIsFinalSaved();
		} else {
			this.setLocalEuLineup();
			this.setLocalUsLineup();
		}
	};

	@action checkAutoFilledLineup() {
		const isEurope = eq(this._selectedRegion, TeamRegion.Europe);

		if (isEurope) {
			return isEmpty(compact(this._euLineup)) ? this.autoFillLineup(TeamRegion.Europe) : noop;
		} else {
			return isEmpty(compact(this._usLineup)) ? this.autoFillLineup(TeamRegion.Usa) : noop;
		}
	}

	@action setPrevStep() {
		const changeRegionValue =
			this._selectedRegion === TeamRegion.Europe ? TeamRegion.Usa : TeamRegion.Europe;

		if (eq(this._currentStep, 2)) {
			this.setSelectedRegion(changeRegionValue);
		}

		if (this.currentStep) {
			this._currentStep = this._currentStep - 1;
		}
	}

	@action setNextStep() {
		const changeRegionValue =
			this._selectedRegion === TeamRegion.Europe ? TeamRegion.Usa : TeamRegion.Europe;

		// if (!this.isCurrentLineupFull) {
		// 	this._modalStore.showModal(ModalType.EMPTY_LINEUP, {
		// 		message: "",
		// 	});
		//
		// 	return;
		// }

		if (eq(this._currentStep, 3)) {
			this.onSaveTeam();
			return;
		}

		if (eq(this._currentStep, 1)) {
			this.setSelectedRegion(changeRegionValue);
			this.checkAutoFilledLineup();

			const isFullEuLineup = size(compact(this._euLineup)) === 12;
			const isFullUsLineup = size(compact(this._usLineup)) === 12;

			const isShownSkipModal = !isFullEuLineup || !isFullUsLineup;

			if (isShownSkipModal) {
				setTimeout(() => {
					this._modalStore.showModal(ModalType.SKIP, {
						message: "",
					});
				}, 1000);
			}

			// if (this._userStore.isAuthorized) {
			// 	void this.onCreateTeam()
			// 		.then(() => this.setSelectedRegion(changeRegionValue))
			// 		.then(() => this.checkAutoFilledLineup());
			// } else {
			//	this.setSelectedRegion(changeRegionValue);
			//	this.checkAutoFilledLineup();
			// }
		}

		if (lt(this.currentStep, 4)) {
			this._currentStep = this._currentStep + 1;
		}
	}

	@action clearPartLineupByStep() {
		const step = this._currentStep;

		this._topRankPlayers = this._topRankPlayers.filter(
			(it) => !includes(this.currentLineup, it)
		);

		if (eq(this._selectedRegion, TeamRegion.Europe)) {
			includes([0, 2], step)
				? (this._euLineup = DEFAULT_LINEUP)
				: (this._euLineup = concat(take(this._euLineup, 6), fill(Array(6), 0)));
		} else {
			includes([0, 2], step)
				? (this._usLineup = DEFAULT_LINEUP)
				: (this._usLineup = concat(take(this._usLineup, 6), fill(Array(6), 0)));
		}
	}

	@action toFirstStep() {
		this._currentStep = 0;
	}

	@action setSelectedRegion(value: TeamRegion) {
		this._selectedRegion = value;
		this._isSelectedStart = true;
	}

	@action setFirstSelectedRegion(value: TeamRegion) {
		this._firstSelectedRegion = value;
	}

	@action setIsSkipActive() {
		this._isSkipActive = true;
	}

	@action clearIsSkipActive() {
		this._isSkipActive = false;
	}

	@action setIsFinalSaved() {
		this._isFinalSaved = true;
	}

	@action clearIsFinalSaved() {
		this._isFinalSaved = false;
	}

	@action setIsTeamSkipped() {
		this._isTeamSkipped = true;
	}

	@action clearIsTeamSkipped() {
		this._isTeamSkipped = false;
	}

	@action setIsTeamSavedAfterLogin() {
		this._isTeamSavedAfterLogin = true;
	}

	@action clearIsTeamSavedAfterLogin() {
		this._isTeamSavedAfterLogin = false;
	}

	@action setRegionTeamSaved() {
		if (eq(this._firstSelectedRegion, TeamRegion.Europe)) {
			this._isEuTeamSaved = true;
		} else {
			this._isUsTeamSaved = true;
		}
	}

	@action clearIsTeamsSaved() {
		this._isEuTeamSaved = false;
		this._isUsTeamSaved = false;
	}

	@action autoFillLineup(region: TeamRegion) {
		const isEurope = eq(region, TeamRegion.Europe);
		const currentLineup = isEurope
			? this._playerStore.getEuropePlayers
			: this._playerStore.getUsaPlayers;

		if (isEurope) {
			return chain(currentLineup)
				.orderBy(["isTop", "worldRanking"], ["desc", "asc"])
				.take(6)
				.map((el, i) => (this._euLineup[i] = el.id))
				.value();
		} else {
			return chain(currentLineup)
				.orderBy("stats.RCUSRank", "asc")
				.take(6)
				.map((el, i) => (this._usLineup[i] = el.id))
				.value();
			// take(currentLineup, 6).map((el, i) => (this._usLineup[i] = el.id));
		}
	}

	@action clearLineup() {
		const isEurope = eq(this._selectedRegion, TeamRegion.Europe);

		if (isEurope) {
			return (this._euLineup = DEFAULT_LINEUP);
		} else {
			return (this._usLineup = DEFAULT_LINEUP);
		}
	}

	@action setTopRankPlayers() {
		const topPlayers = concat(
			take(this._playerStore.getEuropePlayers, 6),
			take(this._playerStore.getUsaPlayers, 6)
		).map(({id}) => id);

		this._onInitTopRankPlayers = topPlayers;
		this._topRankPlayers = topPlayers;
	}

	@action setPlayerTooltipData(isOpen: boolean, message: string) {
		this._playerTooltipData = {isOpen, message};
	}

	@action removePlayer(playerId: number) {
		const isEurope = eq(this.selectedRegion, TeamRegion.Europe);

		this.setIsPromptActive();

		if (isEurope) {
			const index = this._euLineup.findIndex((it) => it === playerId);
			this._euLineup[index] = 0;
		} else {
			const index = this._usLineup.findIndex((it) => it === playerId);
			this._usLineup[index] = 0;
		}

		this._topRankPlayers = this._topRankPlayers.filter((it) => !eq(it, playerId));
		this._removedTopRankPlayers = uniq([...this._removedTopRankPlayers, playerId]);

		// Remove Tooltip add/remove player by task F2P1-32209
		// this.setPlayerTooltipData(
		// 	true,
		// 	`${player?.fullName ?? ""} has been removed from your team`
		// );
		//
		// setTimeout(() => {
		// 	this.setPlayerTooltipData(false, "");
		// }, 2000);
	}

	@action addPlayer(playerId: number) {
		const isEurope = eq(this.selectedRegion, TeamRegion.Europe);

		this.setIsPromptActive();

		if (this.isCurrentLineupFull) {
			return;
		}

		if (isEurope) {
			const index = this._euLineup.findIndex((it) => it === 0);
			this._euLineup[index] = playerId;
		}

		if (!isEurope) {
			const index = this._usLineup.findIndex((it) => it === 0);
			this._usLineup[index] = playerId;
		}

		if (includes([0, 2], this._currentStep)) {
			this._topRankPlayers = [...this._topRankPlayers, playerId];
		}

		// Remove Tooltip add/remove player by task F2P1-32209
		// this.setPlayerTooltipData(true, `${player?.fullName ?? ""} has been added to your team`);
		//
		// setTimeout(() => {
		// 	this.setPlayerTooltipData(false, "");
		// }, 2000);
	}

	@action
	async createTeam(payload: ICreateTeamPayload) {
		try {
			const response = await this._teamApi.create(payload);
			const {team} = response.data.success;

			runInAction(() => {
				if (team.team === TeamRegion.Europe) {
					this._euLineup = team.lineup ? team.lineup : [];
					this._isEuTeamSaved = true;
				} else {
					this._usLineup = team.lineup ? team.lineup : [];
					this._isUsTeamSaved = true;
				}
			});
		} catch (err) {
			this._modalStore.showModal(ModalType.ERROR, {
				title: this._i18nStore.t("modals.error.title", "Error"),
				message: "Ups, something went wrong, please try again later.",
			});
		}
	}

	@action
	async onCreateTeam(region: TeamRegion) {
		const isEurope = eq(region, TeamRegion.Europe);

		if (isEurope) {
			await this.createTeam({
				lang: this.i18n.lang,
				lineup: this._euLineup,
				team: region,
			});
		} else {
			await this.createTeam({
				lang: this.i18n.lang,
				lineup: this._usLineup,
				team: region,
			});
		}
	}
}
